import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Outlet } from 'react-router-dom';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { MobileNav } from './MobileNav';
import { MobileDrawer } from './MobileDrawer';
import { GameWorkspaceDrawer } from '../mobile/GameWorkspaceDrawer';
import { MobileAlertSheet } from '../mobile/MobileAlertSheet';
import { MobileActionSheet } from '../mobile/MobileActionSheet';
import { MobileFAB } from '../mobile/MobileFAB';
import { MobileOpportunityStack } from '../mobile/MobileOpportunityStack';
import { PulseAura } from '../../os/PulseAura';
import { CommandSpine } from '../../os/CommandSpine';
import { IntelligenceRail } from '../../os/IntelligenceRail';
import { PulseStream } from '../../os/PulseStream';
import { CommandBar } from '../../os/CommandBar';
import { OnboardingGuide } from '../../os/OnboardingGuide';
import { ParlaySlip } from '../Parlay/ParlaySlip';
import { useOSStore } from '../../store/useOSStore';

export function AppLayout() {
  const { focusMode, toggleFocusMode, density, commandBarOpen } = useOSStore();
  const [opportunityStackOpen, setOpportunityStackOpen] = useState(false);
  const [offlineDismissed, setOfflineDismissed] = useState(false);
  const isOnline = useOnlineStatus();

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip when typing in an input or when command bar is open
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || commandBarOpen) return;

      // F — toggle focus mode
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFocusMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleFocusMode, commandBarOpen]);

  // Re-show the banner if connection is restored and then lost again
  useEffect(() => {
    if (isOnline) setOfflineDismissed(false);
  }, [isOnline]);

  return (
    <div data-density={density} className="flex flex-col h-screen overflow-hidden bg-surface-0">

      {/* ── Offline banner ────────────────────────────────────────────────────── */}
      {!isOnline && !offlineDismissed && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-900/80 border-b border-amber-700/50 text-amber-200 text-xs flex-shrink-0">
          <span>Connection lost — data may be stale. Check your network.</span>
          <button
            onClick={() => setOfflineDismissed(true)}
            className="text-amber-400 hover:text-amber-100 transition-colors text-sm leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Pulse Aura — omnipresent intelligence strip ─────────────────────── */}
      <PulseAura />

      {/* ── Main OS shell ────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden min-h-0">

        {/* Command Spine — 48px permanent left rail (desktop only) */}
        <div className="hidden md:flex flex-shrink-0">
          <CommandSpine />
        </div>

        {/* Intelligence Rail — 240px collapsible module summary (desktop only) */}
        <div
          className={clsx(
            'hidden md:flex transition-opacity duration-300',
            focusMode && 'opacity-20 hover:opacity-100',
          )}
        >
          <IntelligenceRail />
        </div>

        {/* Workspace canvas — main content area */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 flex flex-col overflow-hidden w-full pb-16 md:pb-0">
            <Outlet />
          </div>
        </main>

        {/* Pulse Stream — right-side intelligence panel (desktop only) */}
        <div
          className={clsx(
            'hidden md:flex transition-opacity duration-300',
            focusMode && 'opacity-20 hover:opacity-100',
          )}
        >
          <PulseStream />
        </div>
      </div>

      {/* ── Mobile navigation ─────────────────────────────────────────────────── */}
      <MobileNav />
      <MobileDrawer />

      {/* ── Mobile intelligence sheets ─────────────────────────────────────────── */}
      <GameWorkspaceDrawer />
      <MobileAlertSheet />
      <MobileActionSheet />

      {/* ── Mobile opportunity overlay ─────────────────────────────────────────── */}
      <MobileFAB onOpen={() => setOpportunityStackOpen(true)} />
      <MobileOpportunityStack
        open={opportunityStackOpen}
        onClose={() => setOpportunityStackOpen(false)}
      />

      {/* ── Global command bar (⌘K) ────────────────────────────────────────────── */}
      <CommandBar />

      {/* ── Progressive onboarding nudges ─────────────────────────────────────── */}
      <OnboardingGuide />

      {/* ── Global parlay slip (cross-sport) ────────────────────────────────────── */}
      <ParlaySlip />
    </div>
  );
}
